import type { LoanFinancialSnapshotV1 } from './loan-financial.types';
import { formatBusinessDate } from './loan-financial.format';
import {
  buildContractualBreakdownRows,
  buildFinancialSummaryCards,
} from './loan-financial.view-model';
import { groupFeeBreakdown } from './loan-financial.labels';
import { Hint, MoneyRow, SectionCard, SkeletonBlock } from './LoanUiBits';

type Props = {
  snap: LoanFinancialSnapshotV1 | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  showFeeDetail?: boolean;
  onToggleFeeDetail?: () => void;
};

export function LoanFinancialSummary({
  snap,
  loading,
  error,
  onRetry,
  showFeeDetail,
  onToggleFeeDetail,
}: Props) {
  if (loading && !snap) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonBlock key={i} className="h-24" />
        ))}
      </div>
    );
  }

  if (error && !snap) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        No se pudo cargar el resumen financiero. {error}
        {onRetry ? (
          <button type="button" onClick={onRetry} className="ml-2 underline">
            Reintentar
          </button>
        ) : null}
      </div>
    );
  }

  if (!snap) return null;

  const cards = buildFinancialSummaryCards(snap);
  const rows = buildContractualBreakdownRows(snap);
  const fees = groupFeeBreakdown(snap.feeBreakdown);
  const asOf = formatBusinessDate(snap.asOf);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <div
            key={c.id}
            className={`rounded-lg border bg-white p-3 ${
              c.id === 'overdue' && Number(snap.overdue.total) > 0
                ? 'border-amber-300'
                : 'border-slate-200'
            }`}
          >
            <p className="text-xs text-slate-500">
              {c.label}
              {c.tooltip ? <Hint text={c.tooltip} /> : null}
            </p>
            <p
              className={`mt-1 text-lg font-semibold tabular-nums ${
                c.emphasize && c.id === 'overdue' ? 'text-amber-800' : 'text-slate-900'
              }`}
            >
              {c.value}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-slate-400">{c.helper}</p>
          </div>
        ))}
      </div>

      <SectionCard
        title="Plan contractual"
        subtitle={`Pendiente del plan al ${asOf}. No es el saldo contable ni la cancelación anticipada.`}
        actions={
          fees.length > 0 && onToggleFeeDetail ? (
            <button
              type="button"
              onClick={onToggleFeeDetail}
              className="text-xs text-blue-700 hover:underline"
            >
              {showFeeDetail ? 'Ocultar detalle de cargos' : 'Ver detalle de cargos'}
            </button>
          ) : null
        }
      >
        <div className="space-y-1.5">
          {rows.map((r) => (
            <MoneyRow
              key={r.label}
              label={r.label}
              value={r.value}
              emphasis={r.label === 'Total'}
              tooltip={r.tooltip}
            />
          ))}
        </div>
        {showFeeDetail && fees.length > 0 ? (
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-1">
            <p className="text-xs font-medium text-slate-600 mb-1">Cargos pendientes</p>
            {fees.map((f) => (
              <MoneyRow
                key={`${f.chargeType}-${f.synthetic ? 'L' : 'T'}`}
                label={f.label}
                value={`$${Number(f.outstanding).toLocaleString('es-AR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
            ))}
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}
